/**
 * 引导提示
 * 在GuidePopover基础上增加展示控制逻辑
 */
import React, { CSSProperties, ReactNode, useState, useRef } from 'react';
import { Flex, Titlebar } from '@tencent/odc';
import {
  OverlayTrigger,
  PopOver,
  Button,
  OverlayTriggerProps,
  PopOverProps,
} from '@tencent/spaui';
import AdqIcon from '@tencent/adq-icons';
import dayjs from 'dayjs';
import { saveCombinedValue, getFromCombinedValue } from '@/utils/storage';
import { FLAGS } from '@/constants/Constants';

interface IGuideTipProps {
  markKey: string; // 唯一标识，用于存储
  showCount?: number; // 展示次数
  startDate?: string; // 开始展示时间，在此之前不展示
  expires?: string; // 过期时间，过期后不展示，可为空
  onConfirm?: () => void;
  onClose?: () => void;
  children?: ReactNode;
  trigger?: ReactNode;
  title?: string;
  content?: ReactNode;
  cancelButton?: boolean;
  cancleLabel?: string;
  submitLabel?: string;
  placement?: OverlayTriggerProps['placement'];
  maxWidth?: PopOverProps['maxWidth'];
  width?: CSSProperties['width'];
  closable?: boolean;
}

// 从聚合value中取值
function getCountByKey(key: any) {
  return +getFromCombinedValue(FLAGS.GUIDE_TIP_STORAGE.GLOBAL_KEY, key, { defaultValue: 0 });
}

const GuideTip: React.FC<IGuideTipProps> = ({
  markKey,
  showCount = 1,
  startDate = '',
  expires = '',
  onConfirm,
  onClose,
  trigger,
  children,
  title,
  content,
  cancelButton = false,
  cancleLabel,
  submitLabel,
  placement = 'top',
  maxWidth = 332,
  width,
  closable,
}, ref) => {
  const [showedCount, setShowedCount] = useState(getCountByKey(markKey));
  const isExpired = useRef(expires ? dayjs().isAfter(dayjs(expires), 'day') : false);
  const startDateEnable = useRef(!startDate ? true : !dayjs().isBefore(dayjs(startDate)));

  const handleAddCount = () => {
    saveCombinedValue(FLAGS.GUIDE_TIP_STORAGE.GLOBAL_KEY, markKey, showedCount + 1);
    setShowedCount(showedCount + 1);
  };

  const handleClose = () => {
    handleAddCount();
    onClose?.();
  };

  const handleConfirm = () => {
    handleAddCount();
    onConfirm?.();
  };

  return (
    <OverlayTrigger
      show={startDateEnable.current && !isExpired.current && showedCount < showCount}
      placement={placement}
      overlay={
        <PopOver
          maxWidth={maxWidth}
          className="l-guide-pop-v3 z-1000"
          style={{ width }}
        >
          <Flex column gap={12}>
            <Flex itemsCenter justifyContent="between">
              <img
                src="https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/1723187651151.svg?max_age=31536000"
                width={40}
                height={18}
                alt=""
              />
              {closable ? (
                <AdqIcon
                  name="cancel"
                  className="text-coolgray-07 hover:text-coolgray-12 cursor-pointer transition-all"
                  onClick={handleClose}
                />
              ) : null}
            </Flex>
            <Flex column gap={6}>
              <Titlebar size="5" title={title} />
              {content}
            </Flex>
            <Flex itemsCenter justifyContent="end" gap={8}>
              {cancelButton ? <Button
                shape="round"
                displayType="link"
                className="meta-link-btn px-12"
                size="small"
                onClick={(e: Event) => {
                  handleClose();
                  e.stopPropagation();
                }}
              >
                {cancleLabel || '取消'}
              </Button> : null}
              <Button
                shape="round"
                className="font-600 px-12"
                displayType="primary"
                size="small"
                onClick={(e: Event) => {
                  handleConfirm();
                  e.stopPropagation();
                }}
              >
                {submitLabel || '我知道了'}
              </Button>
            </Flex>
          </Flex>
        </PopOver>
      }
    >
      <div>{trigger || children}</div>
    </OverlayTrigger>
  );
};


export default GuideTip;
